import React, { useState, useMemo, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTheme } from 'styled-components'
import {
  RESPONSIVE_BREAKPOINT_VERSIONS,
  useResponsiveBreakpointsVersion
} from 'contexts/ResponsiveBreakpointsContext'
import {
  useDesktopMediaQuery,
  useLgDownMediaQuery,
  useMobileMediaQuery
} from 'utils/hooks/useMediaQueries'
import { StyledTextField } from '../Auth/Signup/Steps/Steps.styles'
import {
  Card,
  Error,
  Header,
  ProfileLabelColumn,
  ProfileRoot,
  ProfileRow,
  ProfileTabletValueColumn,
  ProfileValueColumn,
  TextRow
} from './Profile.styles'
import { Tonality } from '@dsny/dsny-component-library'
import { Editsquare, Arrowback } from '@dsny/dsny-component-library/icons'
import { US_PROVINCES, CA_PROVINCES } from '../../constants/provinceList'
import { COUNTRY_LIST } from 'constants/countryList'
import SelectInput from 'components/SelectInput'
import { profileSchema } from './Profile.schema'
import { thunks as ProfileThunks } from 'reducers/Profile'
import NotificationActions from 'reducers/Notification'
import { NotificationType } from 'constants/enums'
import { UnsavedChangesModal } from './UnsavedChangesModal'
import { useNavigate } from 'react-router-dom'

export const Profile = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const isMobile = useMobileMediaQuery()
  const isDesktop = useDesktopMediaQuery()
  const isLgDown = useLgDownMediaQuery()
  const responsiveBreakpointsVersion = useResponsiveBreakpointsVersion()
  const responsiveBreakpointsV2 =
    responsiveBreakpointsVersion === RESPONSIVE_BREAKPOINT_VERSIONS.V2
  const showActionsInCard = responsiveBreakpointsV2 ? !isDesktop : isLgDown
  const [provinces, setProvinces] = useState([])
  const profile = useSelector((state) => state.profile.profile)
  const [isEditing, setIsEditing] = useState(false)
  const [isProvinceRequired, setIsProvinceRequired] = useState(false)
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState(null)
  const langOptions = useSelector((state) => state.setting.langOptions)
  const formRef = useRef(null)
  const { i18n } = useTranslation()

  const {
    control,
    setValue,
    handleSubmit,
    register,
    trigger,
    reset,
    formState: { errors, isDirty }
  } = useForm({
    resolver: yupResolver(profileSchema),
    defaultValues: {}
  })

  const onSubmit = (data) => {
    try {
      saveProfile(data)
      setIsEditing(false)
    } catch (error) {
      console.error('Error submitting form:', error)
    }
  }

  const saveProfile = (data) => {
    dispatch(
      ProfileThunks.postProfile({ ...profile, ...data }, () => {
        i18n.changeLanguage(data?.language)

        dispatch(
          NotificationActions.broadcastMessage(
            '',
            NotificationType.profileUpdated
          )
        )
      })
    )
  }

  const onChangeCountry = (value) => {
    setValue('country', value)
    trigger('country')

    setValue('state', '')
    trigger('state')

    updateProvinceField(value)
  }

  const onChangeProvince = (value) => {
    setValue('state', value)
    trigger('state')
  }

  const countries = useMemo(
    () =>
      COUNTRY_LIST.map((el) => {
        return { value: el.id, label: el.description }
      }),
    []
  )
  const updateProvinceField = (value) => {
    const isCanada = value === 'CA'
    const isUS = value === 'US'
    setIsProvinceRequired(isCanada || isUS)
    if (isUS) {
      setProvinces(
        US_PROVINCES.map((province) => {
          return { value: province.id, label: province.description }
        })
      )
    } else if (isCanada) {
      setProvinces(
        CA_PROVINCES.map((province) => {
          return { value: province.id, label: province.description }
        })
      )
    } else {
      setProvinces([])
    }
  }

  const handleCancelEdit = () => {
    if (isDirty) {
      setPendingNavigation(() => () => {
        resetFormToProfile()
        setIsEditing(false)
      })
      setShowUnsavedModal(true)
    } else {
      setIsEditing(false)
    }
  }

  const resetFormToProfile = () => {
    reset({
      firstname: profile.firstname || '',
      lastname: profile.lastname || '',
      phone: profile.phone || '',
      website: profile.website || '',
      title: profile.title || '',
      company: profile.company || '',
      city: profile.city || '',
      state: profile.state || '',
      country: profile.country || '',
      language: profile.language || 'en'
    })

    if (profile?.country) {
      updateProvinceField(profile.country)
    }
  }

  const requiredFields = ['firstname', 'lastname', 'title']

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isEditing) {
        event.preventDefault()
        event.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isEditing])

  useEffect(() => {
    if (profile) {
      reset({
        firstname: profile.firstname || '',
        lastname: profile.lastname || '',
        phone: profile.phone || '',
        website: profile.website || '',
        title: profile.title || '',
        company: profile.company || '',
        city: profile.city || '',
        state: profile.state || '',
        country: profile.country || '',
        language: profile.language || 'en'
      })
    }
  }, [profile, reset])

  useEffect(() => {
    if (isEditing && profile?.country) {
      updateProvinceField(profile.country)
    }
  }, [isEditing, profile?.country])

  const confirmNavigation = () => {
    setShowUnsavedModal(false)
    if (pendingNavigation) pendingNavigation()
  }

  const cancelNavigation = () => {
    setShowUnsavedModal(false)
  }

  const handleBack = () => {
    if (isEditing && isDirty) {
      setPendingNavigation(() => () => window.history.back())
      setShowUnsavedModal(true)
    } else {
      navigate('/account')
    }
  }

  const languageSelectOptions = useMemo(
    () =>
      langOptions.map((opt) => ({
        value: opt.code,
        label: opt.name
      })),
    [langOptions]
  )

  const renderField = (label, fieldName, value) => (
    <ProfileRow style={{ marginBottom: 12, maxHeight: 70 }}>
      <ProfileLabelColumn>
        <Tonality.UIText
          textStyle="bodyBold"
          color={isEditing ? theme.colors.neutralW100 : theme.colors.neutralW60}
          style={{ fontSize: 14, marginBottom: 4 }}
        >
          {isEditing && requiredFields.includes(fieldName)
            ? `*${label}`
            : label}
        </Tonality.UIText>
      </ProfileLabelColumn>
      {isEditing ? (
        <ProfileValueColumn>
          <StyledTextField
            id={fieldName}
            name={fieldName}
            {...register(fieldName)}
            inputRef={register(fieldName).ref}
            labelText={''}
            error={t(errors[fieldName]?.message)}
          />
        </ProfileValueColumn>
      ) : (
        <ProfileValueColumn>
          <Tonality.UIText
            textStyle="body"
            color={theme.colors.neutralW100}
            style={{ fontSize: 14 }}
          >
            {value || '-'}
          </Tonality.UIText>
        </ProfileValueColumn>
      )}
    </ProfileRow>
  )

  const renderActions = ({ style = {} }) => (
    <div style={{ display: 'flex', gap: 24, ...style }}>
      {isEditing && (
        <Tonality.Button
          onClick={handleCancelEdit}
          variant="text"
          setting="player"
          mode="dark"
          style={{ width: showActionsInCard ? '100%' : 'auto' }}
        >
          {t('general.cancel')}
        </Tonality.Button>
      )}

      <Tonality.Button
        onClick={() => {
          if (isEditing && formRef.current) {
            formRef.current.requestSubmit()
          } else {
            setIsEditing(true)
          }
        }}
        variant="primary"
        setting="player"
        mode="dark"
        style={{ width: showActionsInCard ? '100%' : '130px' }}
      >
        <Editsquare
          width={16}
          height={16}
          color={theme.colors.neutralW20}
          style={{ marginRight: 8 }}
        />

        {isEditing ? t('settings.save') : t('settings.edit-details')}
      </Tonality.Button>
    </div>
  )

  const getLabel = (label, required) => (required ? `*${label}` : label)

  return (
    <ProfileRoot
      $responsiveBreakpointsV2={responsiveBreakpointsV2}
      data-responsive-breakpoints={responsiveBreakpointsVersion}
    >
      <Header>
        <TextRow>
          <Tonality.UIHeading
            headingStyle="headline5"
            color={theme.colors.neutralW100}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            {isMobile && (
              <Arrowback
                width={20}
                height={20}
                color={theme.colors.neutralW100}
                style={{ marginRight: 10 }}
                onClick={handleBack}
              />
            )}

            {t('settings.profile')}
          </Tonality.UIHeading>

          <Tonality.UIText
            textStyle="body"
            color={theme.colors.neutralW100}
            style={{ fontSize: 14 }}
          >
            {t('settings.profile-subtitle')}
          </Tonality.UIText>
        </TextRow>

        {isDesktop && renderActions({ style: { marginLeft: 'auto' } })}
      </Header>

      <Card>
        <div>
          <form
            ref={formRef}
            id="profile-form"
            onSubmit={handleSubmit(onSubmit)}
          >
            {renderField(
              t('general.firstname'),
              'firstname',
              profile?.firstname
            )}

            {renderField(t('general.lastname'), 'lastname', profile?.lastname)}

            {renderField(t('general.phone'), 'phone', profile?.phone)}

            {renderField(t('signup.website'), 'website', profile?.website)}

            {renderField(t('general.position'), 'title', profile?.title)}

            {renderField(t('general.company'), 'company', profile?.company)}

            <ProfileRow style={{ marginBottom: 12, maxHeight: 70 }}>
              <ProfileLabelColumn>
                <Tonality.UIText
                  textStyle="bodyBold"
                  color={
                    isEditing
                      ? theme.colors.neutralW100
                      : theme.colors.neutralW60
                  }
                  style={{ fontSize: 14, marginBottom: 4 }}
                >
                  {getLabel(t('signup.country'), isEditing)}
                </Tonality.UIText>
              </ProfileLabelColumn>
              {isEditing ? (
                <ProfileTabletValueColumn>
                  <Controller
                    name="country"
                    control={control}
                    render={({ field }) => (
                      <SelectInput
                        selectOptions={countries}
                        popoverStyle={{ height: 180, maxHeight: 180 }}
                        value={field.value}
                        onChangeValue={(val) => {
                          field.onChange(val)
                          onChangeCountry(val)
                        }}
                        error={!!errors.country}
                      />
                    )}
                  />
                  {errors.country && (
                    <Error>{t(errors.country?.message)}</Error>
                  )}
                </ProfileTabletValueColumn>
              ) : (
                <ProfileValueColumn>
                  <Tonality.UIText
                    textStyle="body"
                    color={theme.colors.neutralW100}
                    style={{ fontSize: 14 }}
                  >
                    {countries.find((opt) => opt.value === profile?.country)
                      ?.label ||
                      profile?.country ||
                      '-'}
                  </Tonality.UIText>
                </ProfileValueColumn>
              )}
            </ProfileRow>

            <ProfileRow style={{ marginBottom: 12, maxHeight: 70 }}>
              <ProfileLabelColumn>
                <Tonality.UIText
                  textStyle="bodyBold"
                  color={
                    isEditing
                      ? theme.colors.neutralW100
                      : theme.colors.neutralW60
                  }
                  style={{ fontSize: 14, marginBottom: 4 }}
                >
                  {getLabel(
                    t('signup.state-province'),
                    isEditing && isProvinceRequired
                  )}
                </Tonality.UIText>
              </ProfileLabelColumn>
              {isEditing ? (
                <ProfileTabletValueColumn>
                  {isProvinceRequired ? (
                    <Controller
                      name="state"
                      control={control}
                      render={({ field }) => (
                        <SelectInput
                          popoverStyle={{ height: 180, maxHeight: 180 }}
                          selectOptions={provinces}
                          value={field.value}
                          onChangeValue={(val) => {
                            field.onChange(val)
                            onChangeProvince(val)
                          }}
                          error={!!errors.state}
                        />
                      )}
                    />
                  ) : (
                    <StyledTextField
                      id="state"
                      name="state"
                      {...register('state')}
                      ref={null}
                      inputRef={register('state').ref}
                      labelText=""
                      error={errors.state?.message}
                    />
                  )}
                  {errors.state && <Error>{t(errors.state?.message)}</Error>}
                </ProfileTabletValueColumn>
              ) : (
                <ProfileValueColumn>
                  <Tonality.UIText
                    textStyle="body"
                    color={theme.colors.neutralW100}
                    style={{ fontSize: 14 }}
                  >
                    {provinces.find((opt) => opt.value === profile?.state)
                      ?.label ||
                      profile?.state ||
                      '-'}
                  </Tonality.UIText>
                </ProfileValueColumn>
              )}
            </ProfileRow>

            <ProfileRow style={{ marginBottom: 12, maxHeight: 70 }}>
              <ProfileLabelColumn>
                <Tonality.UIText
                  textStyle="bodyBold"
                  color={
                    isEditing
                      ? theme.colors.neutralW100
                      : theme.colors.neutralW60
                  }
                  style={{ fontSize: 14, marginBottom: 4 }}
                >
                  {t('signup.city')}
                </Tonality.UIText>
              </ProfileLabelColumn>
              {isEditing ? (
                <ProfileTabletValueColumn>
                  <StyledTextField
                    id="city"
                    name="city"
                    {...register('city')}
                    ref={null}
                    inputRef={register('city').ref}
                    labelText=""
                    error={!!errors.city && t('signup.city-error')}
                  />
                </ProfileTabletValueColumn>
              ) : (
                <ProfileValueColumn>
                  <Tonality.UIText
                    textStyle="body"
                    color={theme.colors.neutralW100}
                    style={{ fontSize: 14 }}
                  >
                    {profile?.city || '-'}
                  </Tonality.UIText>
                </ProfileValueColumn>
              )}
            </ProfileRow>

            <ProfileRow style={{ marginBottom: 16, maxHeight: 70 }}>
              <ProfileLabelColumn>
                <Tonality.UIText
                  textStyle="bodyBold"
                  color={
                    isEditing
                      ? theme.colors.neutralW100
                      : theme.colors.neutralW60
                  }
                  style={{ fontSize: 14, marginBottom: 4 }}
                >
                  {t('general.language')}
                </Tonality.UIText>
              </ProfileLabelColumn>

              {isEditing ? (
                <ProfileValueColumn>
                  <Controller
                    name="language"
                    control={control}
                    render={({ field }) => (
                      <SelectInput
                        selectOptions={languageSelectOptions}
                        value={field.value}
                        onChangeValue={(val) => {
                          field.onChange(val)
                        }}
                        error={!!errors.language}
                      />
                    )}
                  />
                  {errors.language && (
                    <Error>{t(errors.language?.message)}</Error>
                  )}
                </ProfileValueColumn>
              ) : (
                <ProfileValueColumn>
                  <Tonality.UIText
                    textStyle="body"
                    color={theme.colors.neutralW100}
                    style={{ fontSize: 14 }}
                  >
                    {langOptions.find((opt) => opt.code === profile?.language)
                      ?.name ||
                      profile?.language ||
                      '-'}
                  </Tonality.UIText>
                </ProfileValueColumn>
              )}
            </ProfileRow>
          </form>
        </div>
        {showActionsInCard && renderActions({ style: {} })}
      </Card>
      <UnsavedChangesModal
        isOpen={showUnsavedModal}
        onClose={cancelNavigation}
        onConfirm={confirmNavigation}
      />
    </ProfileRoot>
  )
}

export default Profile
